import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { SharedHttpRequest } from '@reqshare/shared';
import type { ExpirationOption } from '@reqshare/shared';
import { formatCurl } from '@reqshare/shared';
import { getRequest, updateRequestExpiration, deleteRequest } from '../../api/get-request';
import { useWebAuth } from '../../hooks/useWebAuth';
import { HeadersBlock } from '../../components/HeadersBlock/HeadersBlock';
import { BodyBlock } from '../../components/BodyBlock/BodyBlock';
import { ResponseBlock } from '../../components/ResponseBlock/ResponseBlock';
import { MetaInfo } from '../../components/MetaInfo/MetaInfo';
import { CopyButton } from '../../components/CopyButton/CopyButton';
import { OwnerMenu } from '../../components/OwnerMenu/OwnerMenu';
import './RequestPage.scss';

const METHOD_COLORS: Record<string, string> = {
  GET: 'request-page__method--get',
  POST: 'request-page__method--post',
  PUT: 'request-page__method--put',
  PATCH: 'request-page__method--patch',
  DELETE: 'request-page__method--delete',
  HEAD: 'request-page__method--head',
  OPTIONS: 'request-page__method--options',
};

export function RequestPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user } = useWebAuth();
  const [data, setData] = useState<SharedHttpRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      const result = await getRequest(id!, token ?? undefined);

      if (cancelled) return;

      if (result.status === 404) {
        navigate('/not-found', { replace: true });
        return;
      }

      if (result.status === 410) {
        navigate('/expired', { replace: true });
        return;
      }

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }

      setData(result.data!);
      setLoading(false);
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [id, navigate, token]);

  async function handleChangeExpiration(expiration: ExpirationOption) {
    if (!id || !token) return;
    try {
      const result = await updateRequestExpiration(id, expiration, token);
      setData((prev) =>
        prev ? { ...prev, expiresAt: result.expiresAt ?? undefined } : prev,
      );
    } catch (err) {
      console.error('Failed to update expiration:', err);
    }
  }

  async function handleDelete() {
    if (!id || !token) return;
    try {
      await deleteRequest(id, token);
      setDeleted(true);
    } catch (err) {
      console.error('Failed to delete request:', err);
    }
  }

  if (deleted) {
    return (
      <div className="request-page">
        <div className="request-page__container">
          <div className="request-page__deleted">
            <span className="request-page__deleted-icon">&#10005;</span>
            <h2>Request Deleted</h2>
            <p>This shared request has been deleted by its author.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="request-page">
        <div className="request-page__container">
          <div className="request-page__loading">
            <div className="request-page__spinner" />
            <p>Loading shared request...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="request-page">
        <div className="request-page__container">
          <div className="request-page__error">
            <h2>Something went wrong</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const curlCommand = formatCurl(data.request);

  return (
    <div className="request-page">
      <div className="request-page__container">
        <div className="request-page__meta-row">
          <MetaInfo
            createdAt={data.createdAt}
            expiresAt={data.expiresAt}
            viewCount={data.viewCount}
          />
          {data.isOwner && (
            <OwnerMenu
              isPro={user?.subscription?.tier === 'pro'}
              onChangeExpiration={handleChangeExpiration}
              onDelete={handleDelete}
            />
          )}
        </div>

        <section className="request-page__request">
          <div className="request-page__request-line">
            <span className={`request-page__method ${METHOD_COLORS[data.request.method] || ''}`}>
              {data.request.method}
            </span>
            <span className="request-page__url">{data.request.url}</span>
            <span className="request-page__http-version">{data.request.httpVersion}</span>
          </div>

          <div className="request-page__curl-action">
            <CopyButton text={curlCommand} label="Copy as cURL" />
          </div>
        </section>

        <div className="request-page__sections">
          <HeadersBlock title="Request Headers" headers={data.request.headers} />
          <BodyBlock title="Request Body" body={data.request.body} />

          {data.response && (
            <ResponseBlock response={data.response} />
          )}
        </div>
      </div>
    </div>
  );
}
